/**
 * Where a chat lives: this person's account, or this browser.
 *
 * Until phase B1/B2 there was one answer - `localStorage`, for thirty days,
 * gone with a cleared cache and never on another machine. Now the server stores
 * every turn as it is answered (`ai.turn`, written by the chat route itself),
 * and this module is what the screens read through.
 *
 * Three things worth knowing:
 *
 * * **Nothing here writes a chat to the server.** The turn is stored by the
 *   route that produced it, which is the only place that knows what it cost and
 *   which tools it used. The browser copy is still written in browser mode, and
 *   kept in server mode as well - it is what makes a reload instant and an
 *   outage invisible.
 * * **The list carries no transcripts.** A rail of names, counts and totals is
 *   one query; reading every turn of every chat to draw it is how that page
 *   would get slow. Turns arrive when a chat is opened.
 * * **Search goes to the server in server mode**, because the messages are no
 *   longer in the browser to filter - that is what `turn_message_trgm` is for.
 *
 * With no database, or for an account the deployment cannot identify, every
 * call falls back to the browser store and the app behaves exactly as it did
 * before any of this existed. That is rule one of `CLAUDE.md`.
 */

import {
  deleteAssistantConversation,
  getAssistantConversation,
  importAssistantConversations,
  listAssistantConversations,
} from '../../api/endpoints.ts';
import type { AssistantConversation, AssistantConversationTurn } from '../../api/types.ts';
import { loadMe } from '../../state/me.ts';
import { catalogAvailable } from '../../state/store.ts';
import {
  chatTitle,
  chatUsage,
  loadAssistantChats,
  saveAssistantChats,
  type AssistantChatHistory,
  type AssistantHistoryTurn,
} from './assistant-history.ts';

/** Marks the browser store as handed over, so the import runs once per account. */
export const CHAT_IMPORT_DONE_KEY = 'neptune_assistant_chats_imported_v1';

/**
 * One chat as the rail shows it: what it is, what it touched and what it cost -
 * never its transcript.
 */
export interface AssistantChatSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  questions: number;
  ships: string[];
  plots: number;
  exports: number;
  /** Proposals the person actually opened. Browser-only: nothing records it
   *  server-side until `ai.proposal_action` is wired (phase B2). */
  applied: number | null;
  usage: { input: number; output: number; cost: number };
}

let serverMode: boolean | null = null;

/**
 * Whether this account's chats are stored in the database, decided once.
 *
 * Waits for the boot request rather than reading a default: a session that
 * lands straight on the assistant would otherwise decide "no database" before
 * anyone had asked - the same race that filed a whole session's dashboards in
 * `localStorage`.
 */
export async function chatsOnServer(): Promise<boolean> {
  if (serverMode !== null) return serverMode;
  await loadMe().catch(() => {
    /* A boot request that failed is an answer too: the browser store it is. */
  });
  serverMode = catalogAvailable.value;
  return serverMode;
}

/** For tests and for a sign-out: forget which store this session chose. */
export function resetChatStoreMode(): void {
  serverMode = null;
}

function summariseLocal(chat: AssistantChatHistory): AssistantChatSummary {
  const usage = chatUsage(chat.turns);
  let plots = 0;
  let exports = 0;
  let applied = 0;
  const ships = new Set<string>();
  for (const turn of chat.turns) {
    if (turn.plot) {
      plots += 1;
      if (turn.plot.ship_id) ships.add(turn.plot.ship_id);
    }
    if (turn.export) {
      exports += 1;
      if (turn.export.ship_id) ships.add(turn.export.ship_id);
    }
    if (turn.plotApplyClicked || turn.exportApplyClicked) applied += 1;
  }
  return {
    id: chat.id,
    title: chat.title,
    createdAt: chat.createdAt,
    updatedAt: chat.updatedAt,
    questions: chat.turns.filter((t) => t.role === 'user').length,
    ships: [...ships],
    plots,
    exports,
    applied,
    usage,
  };
}

function summariseServer(row: AssistantConversation): AssistantChatSummary {
  return {
    id: row.id,
    title: row.title,
    createdAt: row.started_at,
    updatedAt: row.last_message_at,
    questions: row.turn_count,
    ships: row.ships ?? [],
    plots: row.plots ?? 0,
    exports: row.exports ?? 0,
    applied: null,
    usage: { input: row.input_tokens, output: row.output_tokens, cost: row.cost_usd },
  };
}

/** One stored turn back into the two bubbles the conversation draws. */
function toHistoryTurns(turn: AssistantConversationTurn): AssistantHistoryTurn[] {
  const question: AssistantHistoryTurn = {
    role: 'user',
    content: turn.user_message,
    ...(turn.mentions?.length ? { mentions: turn.mentions } : {}),
    ...(turn.answers ? { answers: turn.answers } : {}),
  };
  if (turn.status === 'error' && !turn.assistant_answer) {
    // A turn that failed is kept as the question it was; the answer never
    // existed, and inventing one here would be a lie about what happened.
    return [question];
  }
  return [
    question,
    {
      role: 'assistant',
      requestId: turn.id,
      content: turn.declined_reason || turn.assistant_answer || '',
      reasoning: turn.assistant_reasoning ?? null,
      plot: turn.proposed_plot ?? null,
      export: turn.proposed_export ?? null,
      ...(turn.card ? { card: turn.card } : {}),
      usage: turn.usage,
    },
  ];
}

/** The history rail. `query` searches titles and questions. */
export async function listChats(query = ''): Promise<AssistantChatSummary[]> {
  if (await chatsOnServer()) {
    try {
      const { conversations } = await listAssistantConversations(query.trim() || undefined);
      return conversations.map(summariseServer);
    } catch {
      // The chats are still in the browser; showing that list beats showing
      // nothing because the archive is unreachable.
    }
  }
  const local = loadAssistantChats().map(summariseLocal);
  const q = query.trim().toLowerCase();
  return q ? local.filter((c) => c.title.toLowerCase().includes(q)) : local;
}

/** A chat's transcript, fetched when it is opened. */
export async function loadChatTurns(id: string): Promise<AssistantHistoryTurn[] | null> {
  if (await chatsOnServer()) {
    try {
      const chat = await getAssistantConversation(id);
      return chat.turns.flatMap(toHistoryTurns);
    } catch {
      /* Fall through: this browser may still have it. */
    }
  }
  return loadAssistantChats().find((chat) => chat.id === id)?.turns ?? null;
}

/**
 * Keep the browser's copy of the open chat.
 *
 * Still written in server mode, deliberately: the server has the authoritative
 * transcript, and this is what makes reopening the drawer instant and an outage
 * invisible. It is pruned to thirty days as it always was.
 */
export function rememberLocally(
  id: string,
  turns: AssistantHistoryTurn[],
  existing: AssistantChatHistory[],
): AssistantChatHistory[] {
  if (!turns.length) return existing;
  const now = new Date().toISOString();
  const previous = existing.find((chat) => chat.id === id);
  const chat: AssistantChatHistory = {
    id,
    title: previous?.title ?? chatTitle(turns),
    createdAt: previous?.createdAt ?? now,
    updatedAt: now,
    turns,
  };
  return saveAssistantChats([chat, ...existing.filter((item) => item.id !== id)]);
}

/** Delete a chat wherever it is stored. The browser copy always goes. */
export async function deleteChat(id: string): Promise<boolean> {
  let ok = true;
  if (await chatsOnServer()) {
    try {
      await deleteAssistantConversation(id);
    } catch {
      ok = false;
    }
  }
  saveAssistantChats(loadAssistantChats().filter((chat) => chat.id !== id));
  return ok;
}

/**
 * Hand this browser's chats to the account, once.
 *
 * The local store is **not** cleared afterwards, only marked as handed over:
 * the cost of keeping it is a few kilobytes, and it is the only copy if an
 * import turns out to have gone somewhere unexpected. Ids are reused, so a
 * second import is a no-op and a chat that is still open keeps writing to the
 * same row.
 */
export async function importBrowserChats(): Promise<number> {
  if (!(await chatsOnServer())) return 0;
  try {
    if (localStorage.getItem(CHAT_IMPORT_DONE_KEY)) return 0;
  } catch {
    return 0;
  }
  const chats = loadAssistantChats();
  if (!chats.length) {
    markImported();
    return 0;
  }
  try {
    const { imported } = await importAssistantConversations(chats);
    markImported();
    return imported;
  } catch (e) {
    // Leave the marker unset so the next load tries again, and leave the local
    // store alone. Nothing here is worth blocking the assistant for.
    console.warn('Could not import browser chats', e);
    return 0;
  }
}

function markImported(): void {
  try {
    localStorage.setItem(CHAT_IMPORT_DONE_KEY, new Date().toISOString());
  } catch {
    /* ignore - it just means the import is attempted again next time */
  }
}
